import { useTranslation } from 'react-i18next';
import { SearchX } from 'lucide-react';
import { Button } from '@/components/ui';
import type { CascadeStage } from '@/hooks/use-transaction-search-cascade';

/**
 * Spec 481 D10 / #482: geteilte Such-End-Meldung fuer drei Flaechen —
 * Voll-Miss, Kaskaden-Fehler-Abbruch und Deep-Link-Fehler (Seiten-Query).
 * Vier Bausteine: (1) Kern-Aussage (Miss mit Achsen-Aufzaehlung | Fehler mit
 * gescheiterter Stufe aus den stages.*-Keys), (2) konditionaler
 * Exakt-Eingabe-Hinweis am Voll-Miss (Begriff matcht keine ID-Form — dann war
 * die Referenz-Achse seine einzige realistische Hoffnung, und die trifft seit
 * #482 nur noch 1:1), (3) konditionale Filter-Nennung, (4) Ein-Klick „ohne
 * Filter erneut suchen". EINE Komponente statt dreier Inline-Triplikate
 * (Drift-Schutz, Trap 9). Der #481-Hinweis „Referenz-Suche eingeschraenkt
 * (Index angefragt)" ist mit dem v2.18-GSI obsolet und zurueckgebaut.
 */
export interface SearchNoticeProps {
  variant: 'miss' | 'error';
  term: string;
  /** Fehler-Variante: gescheiterte Stufe/Achse (Label via `stages.*`). */
  stage?: CascadeStage;
  /** Weder TXN_- noch SIQ/sub_-Gate gematcht (IQ-Laengen-Gate zaehlt nicht). */
  termMatchesNoIdForm?: boolean;
  /** Miss-Variante: mindestens eine Stufe wurde an der Seitenschranke gekappt. */
  sawTruncated?: boolean;
  /** Aktive Server-Facetten → Filter-Nennung + Ohne-Filter-Affordanz. */
  hasServerFilterConstraint: boolean;
  /** Plain-Retry (gleiche Filter) — nur auf den Fehler-Flaechen. */
  onRetry?: () => void;
  onRetryWithoutFilters: () => void;
}

export function SearchNotice({
  variant,
  term,
  stage,
  termMatchesNoIdForm = false,
  sawTruncated = false,
  hasServerFilterConstraint,
  onRetry,
  onRetryWithoutFilters,
}: SearchNoticeProps) {
  const { t } = useTranslation('transactions');

  const withoutFiltersButton = hasServerFilterConstraint && (
    <Button variant="outline" size="sm" onClick={onRetryWithoutFilters}>
      {t('list.search.retryWithoutFilters')}
    </Button>
  );

  if (variant === 'miss') {
    return (
      <div
        role="alert"
        className="mb-4 flex flex-wrap items-center gap-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
      >
        <span className="flex items-start gap-2">
          <SearchX className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            {t('list.search.noResults', { term })}
            {/* #482: Referenz-Suche trifft nur noch exakt — fuer Begriffe ohne
                ID-Form war sie die einzige realistische Achse, der Hinweis
                erklaert den Miss bei teilweise gepasteten Referenzen. */}
            {termMatchesNoIdForm && <> {t('list.search.noResultsReferenceExact')}</>}
            {hasServerFilterConstraint && <> {t('list.search.filterConstraint')}</>}
            {sawTruncated && <> {t('list.search.noResultsTruncated')}</>}
          </span>
        </span>
        {withoutFiltersButton}
      </div>
    );
  }

  return (
    <div
      role="alert"
      className="mb-4 flex flex-wrap items-center gap-3 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive"
    >
      <span>
        {t('list.search.error', { stage: stage ? t(`list.search.stages.${stage}`) : '' })}
        {hasServerFilterConstraint && <> {t('list.search.filterConstraint')}</>}
      </span>
      {onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry}>
          {t('list.search.retry')}
        </Button>
      )}
      {withoutFiltersButton}
    </div>
  );
}
